import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * „Zurück“, das dorthin führt, woher man kam, und das Ziel beim Namen nennt („Zurück zu den
 * Kontakten“).
 *
 * React Router legt die Position im Browser-Verlauf in `history.state.idx` ab. Das Layout merkt sich
 * mit `useVerlaufMerken` je Position die Adresse (in der Session, übersteht ein Neuladen); der Link
 * schaut nach, was eine Position davor lag, und geht mit `navigate(-1)` genau dorthin — so bleiben
 * Filter und Scrollstand der vorigen Seite erhalten. Kam man direkt über eine Adresse (neuer Tab,
 * Lesezeichen), gibt es kein „davor“: dann gilt das feste `fallback`.
 */

const SCHLUESSEL = 'admin_verlauf';

const lesen = (): Record<string, string> => {
  try { return JSON.parse(sessionStorage.getItem(SCHLUESSEL) || '{}'); } catch { return {}; }
};

const position = (): number | undefined => (window.history.state as { idx?: number } | null)?.idx;

/** Im Layout aufrufen: merkt sich die Adresse jeder Verlaufsposition. */
export const useVerlaufMerken = () => {
  const location = useLocation();
  useEffect(() => {
    const idx = position();
    if (idx === undefined) return;
    const verlauf = lesen();
    verlauf[idx] = location.pathname + location.search;
    try { sessionStorage.setItem(SCHLUESSEL, JSON.stringify(verlauf)); } catch { /* dann eben ohne */ }
  }, [location]);
};

// Wie die Seite im Satz heißt: „Zurück <…>“. Unbekannte Seiten bekommen kein Ziel im Namen.
const ZIELE: [RegExp, string][] = [
  [/^\/admin\/outreach\/templates/, 'zu den Vorlagen'],
  [/^\/admin\/outreach/, 'zu den Kontakten'],
  [/^\/events\/manage/, 'zu den Events'],
  [/^\/organizers\/zugaenge/, 'zu den Veranstalter-Zugängen'],
  [/^\/organizers/, 'zu den Veranstaltern'],
  [/^\/admin\/buchungen/, 'zu den Buchungsanfragen'],
  [/^\/admin\/anfragen/, 'zu den Anfragen'],
  [/^\/moderation\/vorschlaege/, 'zu den Vorschlägen'],
  [/^\/moderation\/duplikate/, 'zu den Duplikaten'],
  [/^\/admin\/news/, 'zu den News'],
  [/^\/admin\/rennkalender/, 'zum Rennkalender'],
];

const zielName = (pfad: string) => ZIELE.find(([muster]) => muster.test(pfad))?.[1];

const STIL: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600,
  color: 'var(--text-secondary)', textDecoration: 'none', background: 'none', border: 'none',
  padding: 0, cursor: 'pointer',
};

export const ZurueckLink: React.FC<{ fallback: string; fallbackLabel: string }> = ({ fallback, fallbackLabel }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const idx = position();
  const vorher = idx !== undefined && idx > 0 ? lesen()[idx - 1] : undefined;
  const name = vorher && vorher !== location.pathname + location.search ? zielName(vorher) : undefined;

  if (!name) {
    return <Link to={fallback} style={STIL}><ArrowLeft size={15} aria-hidden />{fallbackLabel}</Link>;
  }
  return (
    <button type="button" onClick={() => navigate(-1)} style={STIL}>
      <ArrowLeft size={15} aria-hidden />Zurück {name}
    </button>
  );
};

export default ZurueckLink;
